import { useMemo, type ReactNode } from "react";
import {
  AcceptanceStatus,
  EmailInviteStatus,
  EWorkflowStatusDataBlock
} from "constants/index";
import { ComputeStrategy } from "components/workflow-template-editor/model";

export type MaterialRequestDurationRow = {
  id?: string;
  workflow_status?: string;
  feature_workflow_instance?: {
    workflow_status?: string;
  } | null;
  assignee_user?: {
    id?: string;
    email?: string;
    status?: number;
  } | null;
  invite_status?: string | number | null;
  template?: {
    dateblock_compute_strategy?: {
      key?: string;
    };
  };
  date_block?: {
    milestones?: Array<{
      projected?: string | null;
    }>;
  } | null;
};

export type MaterialRequestDurationIntent =
  | "lead_time_request"
  | "lead_time_resend"
  | "material_progress_request"
  | "material_progress_resend"
  | "none";

export type MaterialRequestDurationButtonVariant = "edit" | "resend";

export type UseMaterialRequestDurationButtonParams = {
  material: MaterialRequestDurationRow;
  isFeatureEnabled: boolean;
  canRequest: boolean;
};

export type UseMaterialRequestDurationButtonResult = {
  shouldRender: boolean;
  intent: MaterialRequestDurationIntent;
  variant: MaterialRequestDurationButtonVariant;
  isDisabled: boolean;
  buttonClassName: string;
  testId: string;
  tooltipContent: ReactNode;
};

type TooltipLabels = {
  cannotRequest: string;
  requestTitle: string;
  requestDefault: string;
};

const LEAD_TIME_TOOLTIP_LABELS: TooltipLabels = {
  cannotRequest: "Cannot request Lead Time:",
  requestTitle: "Request Lead Time:",
  requestDefault: "Request Lead Time"
};

const MATERIAL_PROGRESS_TOOLTIP_LABELS: TooltipLabels = {
  cannotRequest: "Cannot request Status:",
  requestTitle: "Request Status:",
  requestDefault: "Request Status"
};

function isRequestSent(status: string | number | null | undefined): boolean {
  return status === EmailInviteStatus.REQUEST_SENT;
}

function isCommittedDateTemplate(
  material: MaterialRequestDurationRow
): boolean {
  return (
    material?.template?.dateblock_compute_strategy?.key ===
    ComputeStrategy.COMMITTED_DATE_DRIVEN
  );
}

export function hasAnyMilestoneProjectedDate(
  material: MaterialRequestDurationRow
): boolean {
  return (
    material?.date_block?.milestones?.some((milestone) =>
      Boolean(milestone?.projected)
    ) ?? false
  );
}

function getCommittedDateRequestLabel(
  material: MaterialRequestDurationRow
): string {
  if (
    resolveWorkflowStatus(material) === EWorkflowStatusDataBlock.IN_PROGRESS
  ) {
    return "Request Status";
  }

  return hasAnyMilestoneProjectedDate(material)
    ? "Request Status"
    : "Request Committed Dates";
}

export function resolveWorkflowStatus(
  material: MaterialRequestDurationRow
): string | undefined {
  const workflowStatus =
    material?.workflow_status ??
    material?.feature_workflow_instance?.workflow_status;

  if (workflowStatus == null || workflowStatus === "") {
    return undefined;
  }

  return String(workflowStatus).trim();
}

function getUnsupportedWorkflowMessage(
  workflowStatus: string | undefined
): string {
  if (workflowStatus === EWorkflowStatusDataBlock.WF_COMPLETED) {
    return "Material workflow is completed.";
  }

  if (workflowStatus === EWorkflowStatusDataBlock.VOID) {
    return "Material is void.";
  }

  return "Request is not available for the current workflow status.";
}

export function getMaterialRequestDurationIntent(
  material: MaterialRequestDurationRow
): MaterialRequestDurationIntent {
  const workflowStatus = resolveWorkflowStatus(material);

  if (workflowStatus === EWorkflowStatusDataBlock.WF_NOT_STARTED) {
    return isRequestSent(material?.invite_status)
      ? "lead_time_resend"
      : "lead_time_request";
  }

  if (workflowStatus === EWorkflowStatusDataBlock.IN_PROGRESS) {
    return isRequestSent(material?.invite_status)
      ? "material_progress_resend"
      : "material_progress_request";
  }

  return "none";
}

export function getMaterialRequestDurationTitle(
  material: MaterialRequestDurationRow
): string {
  const intent = getMaterialRequestDurationIntent(material);

  if (intent === "none") {
    return "Request Lead Time/Status";
  }

  if (isCommittedDateTemplate(material)) {
    return getCommittedDateRequestLabel(material);
  }

  return getTooltipLabels(intent).requestDefault;
}

function getTooltipLabels(
  intent: MaterialRequestDurationIntent,
  material?: MaterialRequestDurationRow
): TooltipLabels {
  if (material && isCommittedDateTemplate(material)) {
    const requestLabel = getCommittedDateRequestLabel(material);
    const requestAction =
      requestLabel === "Request Status"
        ? "request Status"
        : "request Committed Dates";
    return {
      cannotRequest: `Cannot ${requestAction}:`,
      requestTitle: requestLabel,
      requestDefault: requestLabel
    };
  }

  if (
    intent === "material_progress_request" ||
    intent === "material_progress_resend"
  ) {
    return MATERIAL_PROGRESS_TOOLTIP_LABELS;
  }

  return LEAD_TIME_TOOLTIP_LABELS;
}

function getRequestAlreadySentForIntent(
  material: MaterialRequestDurationRow,
  intent: MaterialRequestDurationIntent
): boolean {
  if (intent === "lead_time_resend" || intent === "lead_time_request") {
    return isRequestSent(material?.invite_status);
  }

  if (
    intent === "material_progress_resend" ||
    intent === "material_progress_request"
  ) {
    return isRequestSent(material?.invite_status);
  }

  return false;
}

function getTooltipContent({
  labels,
  workflowStatus,
  intent,
  isAssigneePresent,
  isAssigneeUnregistered,
  assigneeIsInactive,
  requestAlreadySent
}: {
  labels: TooltipLabels;
  workflowStatus: string | undefined;
  intent: MaterialRequestDurationIntent;
  isAssigneePresent: boolean;
  isAssigneeUnregistered: boolean;
  assigneeIsInactive: boolean;
  requestAlreadySent: boolean;
}): ReactNode {
  if (!isAssigneePresent) {
    return (
      <div className="w-[200px] text-sm">
        <div>{labels.cannotRequest}</div>
        <br />
        <div>No subcontractor assigned.</div>
      </div>
    );
  }

  if (isAssigneeUnregistered) {
    return (
      <div className="w-[200px] text-sm">
        <div>{labels.cannotRequest}</div>
        <br />
        <div>Unregistered subcontractor assigned.</div>
      </div>
    );
  }

  if (assigneeIsInactive) {
    return (
      <div className="w-[200px] text-sm">
        <div>{labels.cannotRequest}</div>
        <br />
        <div>Assigned subcontractor is inactive.</div>
      </div>
    );
  }

  if (requestAlreadySent) {
    return (
      <div className="w-[200px] text-sm">
        <div>{labels.requestTitle}</div>
        <div className="italic">Request sent.</div>
        <br />
        <div className="italic">Click to resend.</div>
      </div>
    );
  }

  if (intent === "none") {
    return (
      <div className="w-[200px] text-sm">
        <div>{labels.cannotRequest}</div>
        <br />
        <div>{getUnsupportedWorkflowMessage(workflowStatus)}</div>
      </div>
    );
  }

  return <div className="w-[200px] text-sm">{labels.requestDefault}</div>;
}

export function useMaterialRequestDurationButton({
  material,
  isFeatureEnabled,
  canRequest
}: UseMaterialRequestDurationButtonParams): UseMaterialRequestDurationButtonResult {
  const workflowStatus = resolveWorkflowStatus(material);
  const intent = getMaterialRequestDurationIntent(material);
  const tooltipLabels = getTooltipLabels(intent, material);

  const isAssigneePresent = Boolean(material?.assignee_user);
  const isAssigneeUnregistered = !material?.assignee_user?.email;

  const requestAlreadySent = getRequestAlreadySentForIntent(material, intent);

  const assigneeIsInactive = material?.assignee_user?.id
    ? material.assignee_user.status === AcceptanceStatus.DEACTIVATED
    : false;

  const isResendIntent =
    intent === "lead_time_resend" || intent === "material_progress_resend";

  const isResendVariant =
    isResendIntent &&
    isAssigneePresent &&
    !assigneeIsInactive &&
    !isAssigneeUnregistered;

  const requestBtnDisabled =
    intent === "none" ||
    !isAssigneePresent ||
    assigneeIsInactive ||
    isAssigneeUnregistered ||
    requestAlreadySent;

  const shouldRender = isFeatureEnabled && canRequest;
  const variant: MaterialRequestDurationButtonVariant = isResendVariant
    ? "resend"
    : "edit";

  const isDisabled = isResendVariant ? false : requestBtnDisabled;

  const buttonClassName = isResendVariant
    ? "text-ciq-neutral-600 hover:text-ciq-primary-100"
    : requestBtnDisabled
      ? "text-ciq-gray-400 hover:text-ciq-gray-400"
      : "text-ciq-primary-800 hover:text-ciq-primary-800";

  const testId =
    variant === "resend"
      ? "index-button-person-check-request-duration"
      : "index-button-person-edit-request-duration";

  const tooltipContent = useMemo(
    () =>
      getTooltipContent({
        labels: tooltipLabels,
        workflowStatus,
        intent,
        isAssigneePresent,
        isAssigneeUnregistered,
        assigneeIsInactive,
        requestAlreadySent
      }),
    [
      tooltipLabels,
      workflowStatus,
      intent,
      isAssigneePresent,
      isAssigneeUnregistered,
      assigneeIsInactive,
      requestAlreadySent
    ]
  );

  return {
    shouldRender,
    intent,
    variant,
    isDisabled,
    buttonClassName,
    testId,
    tooltipContent
  };
}
